/* Voice input, shared by every place someone answers a question in their own
   words: the AI interview, the gap questions Triton sends a client, and the
   Expert Studio FAQs.

   Extracted rather than copied because the awkward parts are browser bugs — see
   the comments inside — and three copies of a workaround drift apart. */
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/lib/icons';

// Minimal typing for the browser SpeechRecognition API (not in lib.dom yet).
export type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
};

// Turn a SpeechRecognition error code into a plain, actionable message. Safari in
// particular needs system Dictation enabled and often reports 'service-not-allowed'.
function micErrorMessage(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone/voice access is blocked. Allow the mic for this site — on Safari also enable Dictation in System Settings ▸ Keyboard — or just type your answer.';
    case 'audio-capture':
      return 'No microphone was found. Check your mic, or type your answer instead.';
    case 'network':
      return 'The voice service is unavailable right now. Please type your answer.';
    default:
      return 'Voice input stopped unexpectedly. You can tap the mic to retry, or type your answer.';
  }
}
export function getSpeechRecognition(): SpeechRecognitionLike | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

/* Dictation shared by the AI interview and the client's gap answers.

   The awkward parts are browser bugs, and each earns its comment: Safari ends
   recognition after every phrase, Chrome times out around a minute, and Android
   Chrome re-fires onresult with overlapping final results — so the transcript is
   rebuilt from the full snapshot every event rather than appended, and onend
   restarts while the user still wants the mic on. */
export function useDictation(onText: (next: string) => void) {
  const [listening, setListening] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const wantListeningRef = useRef(false);  // user wants the mic on — drives auto-restart
  const baseRef = useRef('');              // text already in the box when the mic started
  const committedRef = useRef('');         // finalized words from previous runs
  const runFinalRef = useRef('');          // finalized words in the CURRENT run

  const stop = () => {
    wantListeningRef.current = false;
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
    setListening(false);
  };

  const start = (current: string) => {
    const rec = getSpeechRecognition();
    if (!rec) {
      setMicError('Voice input isn’t supported in this browser. Please type your answer.');
      return;
    }
    rec.lang = 'en-US';
    rec.interimResults = true;  // words appear live as you speak
    rec.continuous = true;      // keeps listening through pauses (ignored by Safari)
    baseRef.current = current ? `${current} ` : '';
    committedRef.current = '';
    runFinalRef.current = '';
    setMicError(null);

    rec.onresult = (e) => {
      let runFinal = '';
      let interim = '';
      for (let i2 = 0; i2 < e.results.length; i2++) {
        const seg = e.results[i2][0]?.transcript ?? '';
        if (e.results[i2].isFinal) runFinal += `${seg} `;
        else interim += seg;
      }
      runFinalRef.current = runFinal;
      onText(baseRef.current + committedRef.current + runFinal + interim);
    };
    rec.onerror = (e) => {
      const code = e?.error || '';
      // 'no-speech' / 'aborted' happen on ordinary pauses — let onend restart.
      if (code && code !== 'no-speech' && code !== 'aborted') {
        wantListeningRef.current = false;
        setMicError(micErrorMessage(code));
      }
    };
    rec.onend = () => {
      committedRef.current += runFinalRef.current;
      runFinalRef.current = '';
      if (wantListeningRef.current) {
        try { rec.start(); }
        catch { wantListeningRef.current = false; setListening(false); }
      } else {
        setListening(false);
      }
    };
    recognitionRef.current = rec;
    wantListeningRef.current = true;
    try {
      rec.start();
      setListening(true);
    } catch {
      wantListeningRef.current = false;
      setListening(false);
      setMicError('Could not start voice input. Please type your answer.');
    }
  };

  // Never let the auto-restart loop outlive the component that owns it.
  useEffect(() => () => { wantListeningRef.current = false; try { recognitionRef.current?.stop(); } catch { /* noop */ } }, []);

  return { listening, micError, start, stop };
}

/** The mic button, same chrome and icon the interview composer uses. */
export function MicButton({ listening, onToggle }: { listening: boolean; onToggle: () => void }) {
  return (
    <button type="button" className={`cq-mic cq-mic--sm ${listening ? 'is-on' : ''}`} onClick={onToggle}
      aria-label={listening ? 'Stop dictation' : 'Dictate'}
      title={listening ? 'Stop dictation' : 'Speak your answer'}>
      <Icon name="phone" size={14} />
    </button>
  );
}